/* eslint-disable react/prop-types */
import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import moment from 'moment';
import React, { useContext, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import {
  PageShell,
  PageHeader,
  DataTable,
  SearchInput,
  AddButton,
  DayChipFilter,
  SegmentedFilter,
  ActionIcon,
  EntityCell,
  EventCardMobile,
  EventDateCell,
  EventStatusChips,
  EventSnapshotModal,
  Icon,
  tokens,
  zoneAbbr,
} from 'components/v3';
import { AppContext } from 'shared/AppContext';

// ============================================================
// Gen-3 Events — the Upcoming / Past / Drafts pages, composed from
// v3 components and driven by the `filter` prop the routes already
// pass. Day-of-week filtering happens server-side via the `days`
// input added to the events admin queries.
//
// Serves three admin contexts, detected from the route prefix:
// platform (/admin) uses the global admin queries; track and
// employee admin (/admin-track, /admin-employee) use the account-
// scoped queries with the AppContext account id. Account contexts
// have no drafts page.
// ============================================================

const LIMIT = 15;

const EVENT_ROW_FIELDS = `
  id
  name
  status
  isMultiDay
  start_date
  end_date
  day
  date
  month
  year
  listDates
  sale_start
  sale_end
  on_sale_time
  off_sale_time
  tickets_onsale
  next_sale_start
  payout_status
  track {
    id
    name
    logo
    timezone
  }
`;

const GET_UPCOMING_EVENTS = gql`
  query GetEventsAdminV3($input: GetEventsAdminInput!) {
    getEventsAdmin(input: $input) {
      count
      results { ${EVENT_ROW_FIELDS} }
    }
  }
`;

const GET_PAST_EVENTS = gql`
  query GetPastEventsAdminV3($input: GetEventsAdminInput!) {
    getPastEventsAdmin(input: $input) {
      count
      results { ${EVENT_ROW_FIELDS} }
    }
  }
`;

const GET_DRAFT_EVENTS = gql`
  query GetDraftEventsAdminV3($input: GetEventsAdminInput!) {
    getDraftEventsAdmin(input: $input) {
      count
      results { ${EVENT_ROW_FIELDS} }
    }
  }
`;

const GET_UPCOMING_ACCOUNT_EVENTS = gql`
  query GetUpcomingAccountEventsV3($input: GetAccountEventsInput!) {
    getUpcomingEventsByAccount(input: $input) {
      count
      results { ${EVENT_ROW_FIELDS} }
    }
  }
`;

const GET_PAST_ACCOUNT_EVENTS = gql`
  query GetPastAccountEventsV3($input: GetAccountEventsInput!) {
    getPastEventsByAccount(input: $input) {
      count
      results { ${EVENT_ROW_FIELDS} }
    }
  }
`;

const PAGES = {
  upcoming: {
    title: 'Events',
    icon: 'calendar',
    query: GET_UPCOMING_EVENTS,
    root: 'getEventsAdmin',
    emptyLabel: 'No upcoming events match your filters.',
  },
  past: {
    title: 'Past Events',
    icon: 'history',
    query: GET_PAST_EVENTS,
    root: 'getPastEventsAdmin',
    emptyLabel: 'No past events match your filters.',
  },
  drafts: {
    title: 'Drafts',
    icon: 'file-pen',
    query: GET_DRAFT_EVENTS,
    root: 'getDraftEventsAdmin',
    emptyLabel: 'No drafts yet — start a new event to see it here.',
  },
};

// Header lookups for the platform admin's track / series drill-in views.
const GET_TRACK_NAME = gql`
  query GetTrackNameV3($input: GetTrackInput!) {
    getTrack(input: $input) {
      id
      name
      logo
    }
  }
`;

const GET_SERIES_NAME = gql`
  query GetSeriesNameV3($input: GetSeriesDetailInput!) {
    getSeriesDetail(input: $input) {
      id
      name
      logo
    }
  }
`;

// Track / employee admin variants — account-scoped queries, no drafts.
const ACCOUNT_PAGES = {
  upcoming: {
    ...PAGES.upcoming,
    query: GET_UPCOMING_ACCOUNT_EVENTS,
    root: 'getUpcomingEventsByAccount',
  },
  past: {
    ...PAGES.past,
    query: GET_PAST_ACCOUNT_EVENTS,
    root: 'getPastEventsByAccount',
  },
};

const COLUMNS = [
  {
    key: 'date',
    label: 'Date',
    width: 'minmax(140px,0.8fr)',
    skeleton: 'media',
  },
  { key: 'track', label: 'Track', width: 'minmax(190px,1.2fr)' },
  {
    key: 'event',
    label: 'Event',
    width: 'minmax(200px,1.4fr)',
    primary: true,
  },
  { key: 'onsale', label: 'On Sale', width: '118px' },
  { key: 'offsale', label: 'Off Sale', width: '118px' },
  { key: 'status', label: 'Status', width: '132px', skeleton: 'pill' },
  {
    key: 'actions',
    label: 'Actions',
    width: '128px',
    align: 'right',
    skeleton: 'actions',
    actionsCount: 3,
  },
];

// Context-aware route prefix (MANDATORY per CLAUDE.md).
const routePrefixFor = (pathname) =>
  pathname.includes('admin-track')
    ? '/admin-track'
    : pathname.includes('admin-employee')
    ? '/admin-employee'
    : '/admin';

// Platform-admin drill-ins: /admin/track/events/:id and
// /admin/series/events/:id scope the global admin queries to one entity.
const drillScopeFor = (pathname) => {
  if (pathname.includes('/admin/track/events/')) return 'track';
  if (pathname.includes('/admin/series/events/')) return 'series';
  return null;
};

const RANGE_OPTIONS = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
];

// Drill-in header lookups; both are skipped outside their scope.
function useDrillEntity(drillScope, drillId) {
  const { data: trackData } = useQuery(GET_TRACK_NAME, {
    variables: { input: { id: Number(drillId) } },
    skip: drillScope !== 'track',
  });
  const { data: seriesData } = useQuery(GET_SERIES_NAME, {
    variables: { input: { id: Number(drillId) } },
    skip: drillScope !== 'series',
  });
  return (
    (trackData && trackData.getTrack) ||
    (seriesData && seriesData.getSeriesDetail) ||
    null
  );
}

const buildListInput = ({
  page,
  search,
  days,
  accountScoped,
  accountId,
  drillScope,
  drillId,
  upcoming,
}) => {
  const input = { page: String(page), limit: String(LIMIT) };
  if (accountScoped) input.account_id = accountId;
  if (drillScope === 'track') input.track_id = Number(drillId);
  if (drillScope === 'series') input.series_id = Number(drillId);
  // The track/series branches of getEventsAdmin return the full history by
  // default (the legacy pages want that); the drill's Upcoming toggle must
  // ask for the upcoming window and exclude drafts explicitly.
  if (drillScope && upcoming) {
    input.upcoming_only = true;
    input.show_drafts = false;
  }
  if (search) input.queryString = search;
  if (days.length > 0 && days.length < 7) input.days = days;
  return input;
};

// Page header — drill-ins carry the entity's own name and logo.
function EventsPageHeader({ drillScope, entity, config }) {
  if (!drillScope) {
    return <PageHeader icon={config.icon} title={config.title} />;
  }
  const fallback = drillScope === 'track' ? 'Track Events' : 'Series Events';
  return (
    <PageHeader
      icon={config.icon}
      logo={entity && entity.logo}
      title={(entity && entity.name) || fallback}
      subtitle="Events"
    />
  );
}

// Filter row — drill-ins get the Upcoming/Past toggle instead of the day
// chips (a single track or series doesn't need day-of-week narrowing).
function ListFilters({ drillScope, range, onRange, days, onDays }) {
  if (drillScope) {
    return (
      <SegmentedFilter
        value={range}
        onChange={onRange}
        options={RANGE_OPTIONS}
      />
    );
  }
  return <DayChipFilter value={days} onChange={onDays} />;
}

// Account contexts hide the Track column when every loaded event runs at the
// same track (single-track accounts). Series accounts race at a different
// track each week, so their rows span track ids and keep it. Platform admin
// always shows it.
const visibleColumns = (accountScoped, events) => {
  const trackIds = new Set(events.map((e) => e.track && e.track.id));
  if (!accountScoped || trackIds.size > 1) return COLUMNS;
  return COLUMNS.filter((c) => c.key !== 'track');
};

// Row cells must be components (not raw spans built in renderRow) so they
// read the v3 theme PageShell provides — EventsV3 itself renders above that
// provider, so tokens resolved in its body see the wrong (outer app) theme.
function EventNameCell({ name }) {
  const t = tokens(useTheme());
  return (
    <span
      title={name}
      style={{
        fontSize: 14,
        fontWeight: 600,
        color: t.text,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        minWidth: 0,
        overflow: 'hidden',
        overflowWrap: 'anywhere',
      }}
    >
      {name}
    </span>
  );
}

function FaintDash() {
  const t = tokens(useTheme());
  return <span style={{ fontSize: 14, color: t.faint }}>—</span>;
}

// Sale date cell: "Mar 31, 2026" with a small clock + time line underneath.
// Values are the track's stored wall clock, shown verbatim and labeled with
// the track's zone ("10:00 AM CT") — never converted to the viewer's zone.
function SaleCell({ date, time, tz }) {
  const t = tokens(useTheme());
  const parsed = date ? moment(date, 'MM-DD-YYYY') : null;
  if (!parsed || !parsed.isValid()) {
    return <span style={{ fontSize: 14, color: t.faint }}>—</span>;
  }
  const parsedTime = time
    ? moment(time, ['HH:mm:ss', 'HH:mm', 'h:mm A'])
    : null;
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 14,
          lineHeight: '20px',
          color: t.text,
          whiteSpace: 'nowrap',
        }}
      >
        {parsed.format('MMM D, YYYY')}
      </div>
      {parsedTime && parsedTime.isValid() ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 12,
            lineHeight: '17px',
            color: t.faint,
            marginTop: 2,
          }}
        >
          <Icon name="clock" size={12} color={t.faint} />
          {parsedTime.format('h:mm A')}
          {tz ? ` ${tz}` : ''}
        </div>
      ) : null}
    </div>
  );
}

const EventsV3 = ({ filter = 'upcoming' }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { id: drillId } = useParams();
  const appContext = useContext(AppContext);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [days, setDays] = useState([]);
  const [range, setRange] = useState('upcoming');
  const [snapshotEvent, setSnapshotEvent] = useState(null);

  const routePrefix = routePrefixFor(pathname);

  // Drill-ins live on one route, so Upcoming/Past is a toggle instead of
  // sibling pages.
  const drillScope = drillScopeFor(pathname);
  const activeFilter = drillScope ? range : filter;

  // Track / employee admin see their own account's events; the account id
  // comes from the same AppContext the legacy events pages read.
  const accountScoped = !drillScope && routePrefix !== '/admin';
  const accountId =
    appContext && appContext.state ? appContext.state.accountId : null;

  const pages = accountScoped ? ACCOUNT_PAGES : PAGES;
  const config = pages[activeFilter] || pages.upcoming;

  const input = buildListInput({
    page,
    search,
    days,
    accountScoped,
    accountId,
    drillScope,
    drillId,
    upcoming: activeFilter === 'upcoming',
  });

  const { data, loading } = useQuery(config.query, {
    variables: { input },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: accountScoped && !accountId,
  });

  const entity = useDrillEntity(drillScope, drillId);

  const result = data && data[config.root];
  const events = (result && result.results) || [];
  const total = (result && result.count) || 0;
  const pageCount = Math.max(1, Math.ceil(total / LIMIT));

  // Track drill-ins are single-track by definition; account contexts hide
  // the column only when the loaded rows all share one track.
  const columns = visibleColumns(
    accountScoped || drillScope === 'track',
    events
  );

  const onSearch = (value) => {
    setSearch(value);
    setPage(1);
  };
  const onDays = (value) => {
    setDays(value);
    setPage(1);
  };
  const onRange = (value) => {
    setRange(value);
    setPage(1);
  };

  const renderRow = (e) => ({
    date: <EventDateCell event={e} muted={activeFilter === 'past'} />,
    track: e.track ? (
      <EntityCell logo={e.track.logo} title={e.track.name} />
    ) : (
      <FaintDash />
    ),
    event: <EventNameCell name={e.name} />,
    onsale: (
      <SaleCell
        date={e.sale_start}
        time={e.on_sale_time}
        tz={zoneAbbr(e.track && e.track.timezone)}
      />
    ),
    offsale: (
      <SaleCell
        date={e.sale_end}
        time={e.off_sale_time}
        tz={zoneAbbr(e.track && e.track.timezone)}
      />
    ),
    status: <EventStatusChips event={e} kind={activeFilter} />,
    actions: (
      <div style={{ display: 'flex', gap: 3 }}>
        {e.track ? (
          <ActionIcon
            icon="cloud-lightning"
            title="Track forecast"
            onClick={() => history.push(`${routePrefix}/weather/${e.track.id}`)}
          />
        ) : null}
        <ActionIcon
          icon="camera"
          title="Event snapshot"
          onClick={() => setSnapshotEvent(e)}
        />
        <ActionIcon
          icon="sliders-horizontal"
          title={
            activeFilter === 'drafts' ? 'Continue editing' : 'Manage event'
          }
          onClick={() => history.push(`${routePrefix}/events/menu/${e.id}`)}
        />
      </div>
    ),
  });

  return (
    <PageShell>
      <EventsPageHeader
        drillScope={drillScope}
        entity={entity}
        config={config}
      />

      <DataTable
        columns={columns}
        data={events}
        renderRow={renderRow}
        mobileCard={(e, _i, last) => (
          <EventCardMobile
            event={e}
            kind={activeFilter}
            last={last}
            onWeather={
              e.track
                ? () => history.push(`${routePrefix}/weather/${e.track.id}`)
                : null
            }
            onSnapshot={() => setSnapshotEvent(e)}
            onManage={() => history.push(`${routePrefix}/events/menu/${e.id}`)}
          />
        )}
        loading={loading && !result}
        emptyLabel={config.emptyLabel}
        page={page}
        pageCount={pageCount}
        onPageChange={setPage}
        toolbar={
          <>
            <SearchInput
              onChange={onSearch}
              placeholder="Search events..."
              debounce={350}
            />
            {/* Creating events is platform-admin only — track/employee
                admins (account-scoped contexts) just browse. */}
            {!accountScoped ? (
              <AddButton
                title="Create Event"
                to={`${routePrefix}/events/addEvent`}
              />
            ) : null}
          </>
        }
        filter={
          <ListFilters
            drillScope={drillScope}
            range={range}
            onRange={onRange}
            days={days}
            onDays={onDays}
          />
        }
      />

      {snapshotEvent ? (
        <EventSnapshotModal
          event={snapshotEvent}
          kind={activeFilter}
          onClose={() => setSnapshotEvent(null)}
        />
      ) : null}
    </PageShell>
  );
};

export default EventsV3;
